import { AnimatePresence, motion } from 'framer-motion'
import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'

import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { NotFoundError } from '@/modules/shared/components/not-found-error/NotFoundError'
import { ContentPageLayout } from '../content-page-layout/ContentPageLayout'
import { AnimatedContainer } from './ContentProgressPageLayout.styled'

import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import type { ReactElement, ReactNode } from 'react'
import type { SwipeEventData } from 'react-swipeable'
import type { AppHeaderAction } from '@/@bothrs-ui/web/components/navigation/header/AppHeader.types'

type ContentProgressPageLayoutHandle = {
  handleNext: () => void
  handlePrevious: () => void
}

type ActionProps = {
  icon: IconProp
  label: string
}

type Props = {
  headerPrimaryAction?: ActionProps
  headerSecondaryAction?: ActionProps
  title?: string
  index: number
  maxIndex: number
  children: ReactNode

  minIndex?: number
  hideProgressBar?: boolean

  onGoBack?: () => void
  onUpdateIndex: (index: number) => void
}

type ChildProps = {
  onError?: (error: string) => void
  // Add other props if needed
}

const ContentProgressPageLayout = forwardRef(
  (
    {
      headerPrimaryAction,
      headerSecondaryAction,
      title,
      index,
      maxIndex,
      children,
      minIndex = 0,
      hideProgressBar = false,
      onUpdateIndex,
      onGoBack,
    }: Props,
    ref,
  ) => {
    const pageLayoutMainRef = useRef<HTMLDivElement>(null)
    const navigate = useNavigate()

    // Component State
    // ------------------------------------------------------------------------- /
    const [direction, setDirection] = useState<'forward' | 'backward'>()
    const [error, setError] = useState<string>()

    // Utility Methods
    // ------------------------------------------------------------------------- /
    function scrollToTop() {
      pageLayoutMainRef?.current?.scrollTo({
        top: 0,
      })
    }

    // Interaction Methods
    // ------------------------------------------------------------------------- /
    const handlePrevious = useCallback(() => {
      if (index <= 1) {
        return
      }

      scrollToTop()
      setDirection('backward')

      // [Fabian]: Make sure to skip a render cycle so
      // Framer Motion can first set the direction variant in its motion.div component.
      setTimeout(() => {
        setError(undefined)
        onUpdateIndex(index - 1)
      }, 0)
    }, [index, onUpdateIndex])

    useImperativeHandle(ref, () => ({
      handleNext() {
        if (index >= maxIndex) {
          return
        }
        scrollToTop()
        setDirection('forward')

        setTimeout(() => {
          onUpdateIndex(index + 1)
        }, 0)
      },
      handlePrevious,
    }))

    const handleGoBack = useCallback(() => {
      if (onGoBack) {
        onGoBack()
      } else {
        navigate(-1)
      }
    }, [navigate, onGoBack])

    const handleSwipeBack = useCallback(
      (event: SwipeEventData) => {
        if (event.dir === 'Right' && index > 1) {
          handlePrevious()
        }
      },
      [index, handlePrevious],
    )

    const handleError = (error: string) => {
      setDirection('forward')

      setTimeout(() => {
        setError(error)
      }, 0)
    }

    // Template Variables
    // ------------------------------------------------------------------------- /
    const animationVariants = useMemo(
      () => ({
        slideLeft: {
          opacity: 0,
          x: '100%',
        },
        visible: {
          opacity: 1,
          x: 0,
        },
        slideRight: {
          opacity: 0,
          x: '-100%',
        },
      }),
      [],
    )

    const primaryAction = useMemo(
      () =>
        index === 1 || !headerPrimaryAction
          ? undefined
          : ({
              label: headerPrimaryAction.label,
              icon: headerPrimaryAction.icon,
              onClick: handlePrevious,
            } as AppHeaderAction),
      [index, headerPrimaryAction, handlePrevious],
    )

    const secondaryAction = useMemo(
      () =>
        headerSecondaryAction
          ? ({
              label: headerSecondaryAction.label,
              icon: headerSecondaryAction.icon,
              onClick: handleGoBack,
            } as AppHeaderAction)
          : undefined,
      [handleGoBack, headerSecondaryAction],
    )

    return (
      <ContentPageLayout
        mainRef={pageLayoutMainRef}
        header={
          <AppHeader
            primaryAction={primaryAction}
            secondaryAction={secondaryAction}
            progressText={title}
            progress={hideProgressBar ? undefined : { min: minIndex, max: maxIndex, value: index }}
          />
        }
        showHeaderAlways
        onSwipe={handleSwipeBack}
      >
        {error ? (
          <NotFoundError onClick={handlePrevious} />
        ) : (
          <AnimatePresence mode='popLayout' initial={false}>
            {Children.map(children, (child) => (
              <AnimatedContainer
                key={index}
                variants={animationVariants}
                initial={direction === 'forward' ? 'slideLeft' : 'slideRight'}
                animate='visible'
                exit={direction === 'forward' ? 'slideRight' : 'slideLeft'}
                transition={{ type: 'spring', bounce: 0.1, duration: 0.4 }}
              >
                {isValidElement(child)
                  ? cloneElement(child as ReactElement<ChildProps>, {
                      onError: handleError,
                    })
                  : child}
              </AnimatedContainer>
            ))}
          </AnimatePresence>
        )}
      </ContentPageLayout>
    )
  },
)

ContentProgressPageLayout.displayName = 'ContentProgressPageLayout'

const AnimatedContentProgressPageLayout = motion(ContentProgressPageLayout)

export type { ContentProgressPageLayoutHandle }
export { AnimatedContentProgressPageLayout, ContentProgressPageLayout }
